/**
 * Citation — one validated agent citation, rendered as a clickable link.
 *
 * Clicking opens the cited document in the drawer (deep-linkable). The link is
 * a real anchor/button so it is keyboard reachable and announced correctly.
 */
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import type { AgentCitation } from "@records-monitor/shared";
import type { MouseEvent, ReactElement } from "react";

export interface CitationProps {
  citation: AgentCitation;
  onOpen: (citation: AgentCitation) => void;
}

export function Citation({ citation, onOpen }: CitationProps): ReactElement {
  const title = citation.documentTitle ?? "Cited document";
  // A real anchor so it is announced as a link and keyboard reachable. The
  // href deep-links to the cited chunk; clicking opens the drawer in-place.
  const deepLink = `/search?doc=${encodeURIComponent(citation.documentId)}&chunk=${encodeURIComponent(
    citation.chunkId,
  )}`;
  const handleClick = (event: MouseEvent<HTMLAnchorElement>): void => {
    event.preventDefault();
    onOpen(citation);
  };
  return (
    <ListItem disableGutters alignItems="flex-start">
      <ListItemText
        primary={
          <Box component="span">
            <Box component="span" sx={{ fontWeight: 600, mr: 0.5 }}>
              [{citation.marker}]
            </Box>
            <Link
              href={deepLink}
              underline="hover"
              onClick={handleClick}
              sx={{ verticalAlign: "baseline" }}
            >
              {title}
            </Link>
          </Box>
        }
        secondary={
          <>
            {citation.jurisdictionName ? `${citation.jurisdictionName} — ` : ""}
            {citation.snippet}
          </>
        }
      />
    </ListItem>
  );
}
